// src/lib/similaridade.js
// Analise de QUALIDADE de nomes (setor/chave), client-side, sem IA externa.
// Similaridade robusta (bigramas + palavras + edicao) e deteccao de nome
// generico, rascunho/teste e nome pobre. Nao bloqueia: devolve um veredito
// graduado + motivos para o gestor decidir.
import { norm } from './dom.js';

function bigramas(s) { const r = []; for (let i = 0; i < s.length - 1; i++) r.push(s.slice(i, i + 2)); return r; }

// Coeficiente de Dice sobre bigramas (pega trocas internas/typos leves).
function dice(a, b) {
  const ba = bigramas(a), bb = bigramas(b);
  if (!ba.length || !bb.length) return a === b ? 1 : 0;
  const mapa = new Map();
  for (const g of ba) mapa.set(g, (mapa.get(g) || 0) + 1);
  let inter = 0;
  for (const g of bb) { const c = mapa.get(g) || 0; if (c > 0) { inter++; mapa.set(g, c - 1); } }
  return (2 * inter) / (ba.length + bb.length);
}

// Distancia de Levenshtein normalizada (pega erro de digitacao: 1 letra a mais/menos/trocada).
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n];
}
function levSim(a, b) { const L = Math.max(a.length, b.length); return L ? 1 - levenshtein(a, b) / L : 0; }

function tokens(s) { return s.split(/\s+/).filter(Boolean); }
// Jaccard de palavras (pega reordenacao e mesmas palavras com pontuacao diferente).
function jaccard(a, b) {
  const A = new Set(tokens(a)), B = new Set(tokens(b));
  if (!A.size || !B.size) return 0;
  let inter = 0; for (const t of A) if (B.has(t)) inter++;
  return inter / (A.size + B.size - inter);
}

/** 0..1 - o quanto dois nomes se parecem (maior dos 3 metodos). Ignora maiusculas/acentos. */
export function similaridade(a, b) {
  a = norm(a || ''); b = norm(b || '');
  if (!a || !b) return 0;
  if (a === b) return 1;
  return Math.max(dice(a, b), jaccard(a, b), levSim(a, b));
}

/** Itens mais parecidos com `nome` (acima de `min`), do maior pro menor (max 5). */
export function maisParecidos(nome, lista, getNome, min = 0.5) {
  return (lista || []).map((it) => ({ it, score: similaridade(nome, getNome(it)) }))
    .filter((x) => x.score >= min)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}

// ---- Sinais de qualidade ----
const GENERICOS = new Set(['sala','salas','almoxarifado','deposito','chave','chaves','porta','portas','setor','setores','armario','armarios','banheiro','copa','area','andar','predio','bloco','escada','elevador','corredor','recepcao','quarto','laboratorio','consultorio','arquivo','cozinha','vestiario','geral','local','ambiente']);
const STOP = new Set(['de','da','do','das','dos','e','a','o','as','os','no','na','nos','nas','um','uma']);
const TESTE = new Set(['teste','testes','test','tst','xxx','xpto','asdf','qwerty','abc','exemplo','nome','foo','bar','aaa','aaaa','zzz','nada','sim','nao']);
const TECLADO = ['abcdefghijklmnopqrstuvwxyz','qwertyuiop','asdfghjkl','zxcvbnm','0123456789'];

/** True se o nome e generico demais (so palavras vagas/ligacao e sem numero). */
export function nomeGenerico(nome) {
  const palavras = tokens(norm(nome || ''));
  if (!palavras.length) return false;
  if (palavras.some((p) => /\d/.test(p))) return false;       // tem numero = especifico
  return palavras.every((p) => GENERICOS.has(p) || STOP.has(p));
}

/** True se parece rascunho/teste/digitacao aleatoria (poupa siglas reais tipo UTI, CME, PS). */
export function pareceRascunho(nome) {
  const n = norm(nome || '');
  const s = n.replace(/\s+/g, '');
  if (!s) return false;
  if (TESTE.has(n)) return true;
  if (/^(.)\1{2,}$/.test(s)) return true;                      // 3+ do mesmo caractere: aaa, 1111
  for (const fila of TECLADO) {                                // sequencia de teclado (>=4)
    for (let i = 0; i + 4 <= fila.length; i++) { if (s.includes(fila.slice(i, i + 4))) return true; }
  }
  if (s.length >= 6 && new Set(s).size <= 2) return true;      // baixa diversidade num nome longo
  if (s.length >= 5 && !/[aeiou]/.test(s) && !/\d/.test(s)) return true; // consoantes sem vogal (siglas curtas <=4 escapam)
  return false;
}

/**
 * Veredito de qualidade do nome proposto contra os ja existentes.
 * @returns {{ nivel:'ok'|'atencao'|'forte', topPct:number, motivos:{sev:string,texto:string}[], similares:{nome:string,pct:number}[] }}
 */
export function analisarNome(nome, existentes = [], getNome = (x) => x) {
  const limpo = (nome || '').trim();
  const motivos = [];
  const similares = maisParecidos(limpo, existentes, getNome, 0.55)
    .map((x) => ({ nome: getNome(x.it), pct: Math.round(x.score * 100) }));
  const topPct = similares.length ? similares[0].pct : 0;

  if (pareceRascunho(limpo)) motivos.push({ sev: 'forte', texto: 'Parece um nome de teste/rascunho - use o nome real do setor/chave.' });
  const letras = norm(limpo).replace(/[^a-z0-9]/g, '');
  if (letras.length && letras.length < 3) motivos.push({ sev: 'atencao', texto: 'Nome muito curto - deixe claro qual e o setor/chave.' });
  if (nomeGenerico(limpo)) motivos.push({ sev: 'atencao', texto: 'Generico demais (so palavras vagas) - acrescente numero, andar, sala ou setor para diferenciar.' });

  let nivel = 'ok';
  if (topPct >= 85 || motivos.some((m) => m.sev === 'forte')) nivel = 'forte';
  else if (topPct >= 65 || motivos.length) nivel = 'atencao';
  return { nivel, topPct, motivos, similares };
}